import api from 'Api/ApiMethods';
import i18next from 'i18next';
import { makeAutoObservable } from 'mobx';
import { getAxiosErrorMessage } from 'utils/httpErrorsHelpers';
import { ActionParams, ActionType } from 'models/actions';
import { GrowerSystem } from 'models/grower';
import { getSafeArray } from 'utils/arrayUtils';
import { modeToLabel } from 'utils/actionsUtills';
import { RootStore } from './rootStore';
import { LoadableField } from './types/types';

export interface SensorsPrerequisites {
  // mandatorySensors: MandatorySensors[];
  // oneOfList: NestedArray<string>;
  optionalSensors: string[];
  displayName: string;
}

export class ActionsStore {
  rootStore: RootStore = {} as RootStore;

  actionsCall: LoadableField<ActionType[]> = {
    data: [],
    loading: false,
  };

  actions: ActionType[] = [];
  selectedActions: ActionType[] = [];
  systems: GrowerSystem[] = [];
  systemsCall: LoadableField<GrowerSystem[]> = {
    data: [],
    loading: false,
  };

  isRemovingAction = false;
  actionParamsCall: LoadableField<any[]> = {
    data: [],
    loading: false,
  };

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  setSelectedActions = async (newSelectedActions: ActionType[]) => {
    this.selectedActions = newSelectedActions;
  };

  getActions = async (growerId: number, controller?: AbortController) => {
    this.clearActions();
    this.actionsCall.loading = true;
    this.systemsCall.loading = true;

    try {
      const response = await api.getActions(growerId, controller);
      this.actionsCall.data = getSafeArray(response?.actions);
      this.systemsCall.data = Object.values(response?.systems ?? {});
    } catch {
      this.rootStore?.snackBarStore.showToast({
        detail: i18next.t('errors:something_went_wrong'),
      });
    } finally {
      this.actionsCall.loading = false;
      this.systemsCall.loading = false;
    }
  };

  removeAction = async (systemId: number, actionId: number): Promise<void> => {
    this.isRemovingAction = true;

    try {
      await api.deleteAction(systemId, actionId);
      this.selectedActions = [];
      this.actionsCall.data = this.actionsCall.data.filter(
        (action) => action.id !== actionId,
      );

      this.successToast(i18next.t('grower:remove_action_success_message'));
    } catch (error) {
      this.errorToast(this.getErrorMessage(error));
    } finally {
      this.isRemovingAction = false;
    }
  };

  getActionParams = async (
    actionId: number | undefined,
    systemId: number,
    systemType: string,
  ) => {
    this.actionParamsCall.loading = true;
    try {
      const response = await api.getActionParams(
        actionId,
        systemId,
        systemType,
      );

      this.actionParamsCall.data = response;
      return response;
    } catch (error) {
      this.errorToast(this.getErrorMessage(error));
      return undefined;
    } finally {
      this.actionParamsCall.loading = false;
    }
  };

  postAction = async (action: ActionParams) => {
    this.actionParamsCall.loading = true;
    try {
      const newAction = await api.postAction(action);
      this.actionsCall.data = [newAction, ...this.actionsCall.data];
      this.successToast(i18next.t('grower:add_action_success_message'));
    } catch (error) {
      this.errorToast(this.getErrorMessage(error));
    } finally {
      this.actionParamsCall.loading = false;
    }
  };

  updateAction = async (actionId: number, systemId: number, action: ActionType) => {
    this.actionParamsCall.loading = true;
    try {
      await api.putAction(actionId, systemId, action);
      const filteredActions = (this.actionsCall.data || []).filter(
        (actionObject) => actionObject.id !== actionId,
      );

      const newActionMode = modeToLabel(action.mode);
      const newAction = { ...action, mode: newActionMode }
      this.actionsCall.data = [newAction, ...filteredActions];
      this.successToast(i18next.t('grower:update_action_success_message'));
    } catch (error) {
      this.errorToast(this.getErrorMessage(error));
    } finally {
      this.actionParamsCall.loading = false;
    }
  };

  clearActions = () => {
    this.actionsCall.data = [];
    this.systemsCall.data = [];
  };

  private successToast(message: string): void {
    this.rootStore?.snackBarStore.showToast({
      detail: message,
      severity: 'success',
      summary: 'Success',
    });
  }

  errorToast = (message: string) => {
    this.rootStore?.snackBarStore.showToast({
      detail: message,
    });
  };

  private getErrorMessage(error: unknown): string {
    return (
      getAxiosErrorMessage(error) ?? i18next.t('errors:something_went_wrong')
    );
  }
}
